import React, { FC, useEffect, useState } from 'react';
import { ArrowDropUp, ArrowDropDown, ContentCopy } from '@mui/icons-material';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';

import { ICampaignImage } from 'reduxState/store/campaigns/types';
import { useUploadCampaignImageMutation } from 'reduxState/store/campaigns/api';
import { tryCatch } from 'helpers/tryCatch';

const MAX_DIMENSION_PX = 1920;
const MAX_FILE_SIZE_BEFORE_RESIZE = 2 * 1024 * 1024; // 2MB
/** Max file size accepted by the campaign image dropzone (50MB); larger files are rejected before resize. */
const MAX_CAMPAIGN_IMAGE_UPLOAD_SIZE = 50 * 1024 * 1024;
const JPEG_QUALITY = 0.88;

/**
 * Resizes large images for web display. If the image is small enough, returns the original file.
 * Otherwise scales so the longest side is at most MAX_DIMENSION_PX and compresses as JPEG.
 */
function resizeImageForWeb(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return Promise.resolve(file);
  }

  const needsResize = (width: number, height: number) =>
    width > MAX_DIMENSION_PX || height > MAX_DIMENSION_PX || file.size > MAX_FILE_SIZE_BEFORE_RESIZE;

  return new Promise<File>(resolve => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = img;

      if (!needsResize(width, height)) {
        resolve(file);
        return;
      }

      const scale = Math.min(MAX_DIMENSION_PX / width, MAX_DIMENSION_PX / height, 1);
      const w = Math.round(width * scale);
      const h = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);

      canvas.toBlob(
        blob => {
          if (!blob) {
            resolve(file);
            return;
          }
          const name = file.name.replace(/\.[^.]+$/, '.jpg');
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    img.src = url;
  });
}

interface ICampaignManagerImageUploadProps {
  uploadedImages: ICampaignImage[];
  setUploadedImages: (images: ICampaignImage[]) => void;
  disabled?: boolean;
}

interface IImagePreviewModalProps {
  file: ICampaignImage;
  onClose: () => void;
}

const ImagePreviewModal: FC<IImagePreviewModalProps> = ({ file, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 backdrop-blur-sm"
      onClick={onClose}
    >
      <img
        src={file.url}
        alt={`Full preview of ID: ${file.id}`}
        className="max-w-[90%] max-h-[90%] object-contain"
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
};

const CampaignManagerImageUpload: FC<ICampaignManagerImageUploadProps> = ({
  uploadedImages,
  setUploadedImages,
  disabled,
}) => {
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const [previewFile, setPreviewFile] = useState<ICampaignImage | null>(null);
  const [uploadCampaignImageCallback] = useUploadCampaignImageMutation();

  const handleUploadImage = async (files: File[]) => {
    const resizedFiles = await Promise.all(files.map(resizeImageForWeb));
    const uploadPromises = resizedFiles.map(file =>
      tryCatch(uploadCampaignImageCallback({ image: file }).unwrap())
    );
    const results = await Promise.all(uploadPromises);
    const errors = results.filter(result => result.error);

    const newUploadedImages: ICampaignImage[] = [];
    results.forEach(result => {
      if (result.data && !result.error) {
        newUploadedImages.push(result.data);
      }
    });

    if (errors.length > 0) {
      toast.error(`Error occurred while uploading ${errors.length} image(s)`);
    }

    if (newUploadedImages.length > 0) {
      toast.success(`${newUploadedImages.length} image(s) uploaded successfully`);
      setUploadedImages([...uploadedImages, ...newUploadedImages]);
    }
  };

  const handleCopyImageUrl = async (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Image URL copied to clipboard');
    } catch {
      toast.error('Failed to copy URL to clipboard');
    }
  };

  return (
    <div className="my-2 rounded-md shadow-md">
      <div className={`rounded-md hover:bg-gray-50 p-2 ${isAccordionExpanded ? 'rounded-t' : ''}`}>
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsAccordionExpanded(!isAccordionExpanded)}
        >
          <h5 className="text-muted-dark-purple">Images</h5>
          {isAccordionExpanded ? <ArrowDropUp /> : <ArrowDropDown />}
        </div>
      </div>
      {isAccordionExpanded && (
        <div>
          {!disabled && (
            <Dropzone
              maxSize={MAX_CAMPAIGN_IMAGE_UPLOAD_SIZE}
              accept={{
                'image/*': [
                  '.png',
                  '.jpg',
                  '.jpeg',
                  '.gif',
                  '.bmp',
                  '.tiff',
                  '.tif',
                  '.ico',
                  '.webp',
                  '.avif',
                  '.heic',
                  '.heif',
                  '.svg',
                ],
              }}
              onDrop={acceptedFiles => handleUploadImage(acceptedFiles)}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps({
                    className:
                      'm-2 flex flex-col items-center justify-center w-auto h-[200px] border-2 border-dashed border-gray-300 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200 transition-colors',
                  })}
                >
                  <input {...getInputProps({})} />
                  <p>Drag 'n' drop or click to select files</p>
                </div>
              )}
            </Dropzone>
          )}
          <div className="flex flex-row">
            {uploadedImages.map((file, index) => (
              <div className="flex flex-col" key={index}>
                <div className="p-2 m-2 border border-gray-200 rounded-md" style={{ maxWidth: '200px' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium truncate" style={{ maxWidth: '150px' }}>
                      ID: {file.id}
                    </span>
                    {!disabled && (
                      <button
                        type="button"
                        className="text-xs text-red-500 hover:text-red-700"
                        onClick={() => {
                          const filteredImages = uploadedImages.filter((_, i) => i !== index);
                          setUploadedImages(filteredImages);
                        }}
                        data-testid="remove-image-button"
                      >
                        X
                      </button>
                    )}
                  </div>
                  <div
                    className="relative w-full h-20 mx-auto overflow-hidden rounded-md cursor-pointer"
                    onClick={() => setPreviewFile(file)}
                  >
                    <img src={file.url} alt={`Preview of ${file.id}`} className="object-contain w-full h-full" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 transition-opacity bg-black opacity-0 hover:opacity-100 bg-opacity-30">
                      <span className="text-xs text-center text-white">Click to enlarge</span>
                      <button
                        type="button"
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-muted-dark-purple rounded hover:bg-dark-purple"
                        onClick={e => handleCopyImageUrl(e, file.url)}
                        title="Copy image URL to clipboard"
                        data-testid="copy-image-url-button"
                      >
                        <ContentCopy fontSize="small" />
                        Copy URL
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {previewFile && <ImagePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
    </div>
  );
};

export default CampaignManagerImageUpload;
